import { Box, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StepList } from 'src/features/step-list';
import { DiscountBanner } from 'src/components/discount-banner';
import { PaymentPlan } from 'src/features/payment-plan';
import { RoomCard } from 'src/features/room-card';
import { StepEnum } from 'src/features/step-list/constants';
import { Currency } from 'src/shared/enum/Currency';
import { useGetAmendData } from 'src/lib/hooks/use-get-amend-data';
import { Spinner } from 'src/components/spinner';
import { FailurePage } from 'src/pages/failure';
import { setCreatedPaymentPlan, setUserChoices } from 'src/store/slices/payment-plan-slice';
import { setBuildingData, setRoomData } from 'src/store/slices/booking-slice';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { BookingUpdateFailurePage } from 'src/pages/booking-update-failure';
import { BookingUpdateExpiredPage } from 'src/pages/booking-update-expired';
import { BookingUpdatedPage } from 'src/pages/booking-updated';
import { usePostAmendNoAdminFee } from 'src/lib/hooks/use-post-amend-no-admin-fee';
import { ProcessAmendDto } from 'src/api/amendService/dto/ProcessAmend.dto';
import { ProcessMicroPaymentDto } from 'src/api/sagepay/dto/ProcessMicroPayment.dto';
import { selectUserDetails } from 'src/store/slices/user-slice';
import { usePostProcessMicroPayment } from 'src/lib/hooks/use-post-process-micro-payment';
import { RoommateForm } from 'src/features/multi-occupancy';
import { RoommateFormValues } from 'src/features/multi-occupancy/constants';
import { selectRoommates, updateBasketRoommates } from 'src/store/slices/basket-slice';
import { setAmendRoommatesStorage } from 'src/utils/local-storage-versioning';

import { styles as bookingDetailsStyles } from '../../booking-details/styles';
import { AgreementsPage } from '../../agreements';

export type AmendBookingPageProps = {
    vitaRef: string;
};

export const AmendBookingPage: FC<AmendBookingPageProps> = ({ vitaRef }) => {
    const dispatch = useDispatch();

    const user = useSelector(selectUserDetails);
    const roommates = useSelector(selectRoommates);

    const {
        data: amendData,
        isFetching: isAmendDataFetching,
        error: amendError,
    } = useGetAmendData(vitaRef);

    const {
        mutate: postProcessMicroPayment,
        isPending: isProcessingMicroPayment,
        data: microPaymentData,
    } = usePostProcessMicroPayment();

    const {
        mutate: postAmendNoAdminFee,
        isPending: isPostingAmend,
        isSuccess: successfulAmend,
        error: postAmendError,
    } = usePostAmendNoAdminFee();
    const processError = (error: AxiosError) => {
        switch (error.message) {
            case ErrorMessage.AMEND_NOT_REQUESTED:
            case ErrorMessage.AMEND_ADMIN_FEE_REQUIRED:
            case ErrorMessage.AMEND_GENERIC_ERROR:
                return <BookingUpdateFailurePage />;
            case ErrorMessage.AMEND_WINDOW_EXPIRED:
                return <BookingUpdateExpiredPage />;
            case ErrorMessage.AMEND_ALREADY_PROCESSED:
                return <BookingUpdatedPage />;
            case ErrorMessage.AMEND_TOO_MANY_OCCUPANTS:
                return (
                    <BookingUpdateFailurePage
                        titleKey="booking_updated_amend_occupant_error_title"
                        subtitleKey="booking_updated_amend_occupant_error_content"
                    />
                );
            default:
                return <FailurePage />;
        }
    };

    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState<StepEnum | null>(null);

    useEffect(() => {
        if (!amendData) return;

        setActiveStep(amendData.dual_occupant_amend ? StepEnum.Roommates : StepEnum.Agreements);
    }, [amendData]);

    if (isAmendDataFetching) return <Spinner />;

    if (amendError) {
        return processError(amendError);
    }

    if (postAmendError) {
        return processError(postAmendError);
    }

    if (!amendData) return <FailurePage />;

    if (!user) return <FailurePage />;

    const term =
        amendData.start_date && amendData.end_date && amendData.term_length
            ? {
                  name: '',
                  startDate: amendData.start_date,
                  endDate: amendData.end_date,
                  instalments: [],
                  premium: '',
                  duration: amendData.term_length,
              }
            : null;

    dispatch(setCreatedPaymentPlan(amendData.payment_plan));
    dispatch(
        setUserChoices({
            numberOfStudents: amendData.number_of_students,
            term,
            instalmentType: null,
            instalmentId: null,
        })
    );

    dispatch(setRoomData(amendData.room));
    dispatch(setBuildingData(amendData.building));

    const steps = [StepEnum.Agreements];

    if (amendData.admin_fee_required) steps.push(StepEnum.Payment);
    if (amendData.dual_occupant_amend) steps.unshift(StepEnum.Roommates);

    const hasAdminFee = amendData.admin_fee_required;
    const hasDiscount = !!amendData.payment_plan.promotion;

    const processAmendNoAdminFee = async () => {
        const payload = new ProcessAmendDto(vitaRef, null, roommates);
        postAmendNoAdminFee(payload);
    };

    const processAdminFee = async () => {
        const payload = new ProcessMicroPaymentDto(
            user.firstName,
            user.lastName,
            user.email,
            amendData.room.room_code,
            amendData.room.academic_year,
            vitaRef
        );

        postProcessMicroPayment(payload);
    };

    if (microPaymentData) {
        window.location.href = microPaymentData.redirect_url;
    }

    if (successfulAmend) return <BookingUpdatedPage />;

    return (
        <Box css={bookingDetailsStyles.layout}>
            <Box css={bookingDetailsStyles.leftSide}>
                {activeStep && <StepList activeStep={activeStep} steps={steps} />}
                {activeStep === StepEnum.Roommates && (
                    <RoommateForm
                        onFormSubmit={(values: RoommateFormValues) => {
                            dispatch(updateBasketRoommates(values.roommates));
                            setAmendRoommatesStorage(values.roommates);
                            setActiveStep(StepEnum.Agreements);
                        }}
                        loading={false}
                    />
                )}

                {activeStep === StepEnum.Agreements && (
                    <AgreementsPage
                        hideBackButton={!amendData.dual_occupant_amend}
                        onGoBack={() => setActiveStep(StepEnum.Roommates)}
                        hasRoommates={false}
                        onSubmit={hasAdminFee ? processAdminFee : processAmendNoAdminFee}
                        loading={isPostingAmend || isProcessingMicroPayment}
                        error={null}
                        adminFee={hasAdminFee}
                    />
                )}
            </Box>

            <Box css={bookingDetailsStyles.rightSide}>
                <RoomCard withSmallImage roomData={amendData.room} isAgreementsPage />
                <Box css={bookingDetailsStyles.paymentPlanWrapper}>
                    {hasDiscount && (
                        <DiscountBanner>
                            <Typography variant="body3">{t('discount_applied_info')}</Typography>
                        </DiscountBanner>
                    )}
                    <Box css={bookingDetailsStyles.overviewPayment}>
                        <PaymentPlan
                            showCta={false}
                            withHeaderStipe={false}
                            currency={
                                amendData ? (amendData.room.currency as Currency) : Currency.GBP
                            }
                            disabled={false}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
